// AUTOMATIC RESOLUTION (owner ruling 2026-10-17: OFF on desktop, ON in VR —
// Quest and PCVR — and on phones; APP_PLAYER.md "Automatic resolution").
//   XR      before any session: renderer.xr.setFramebufferScaleFactor(
//           renderconfig framebuffer_scale) — the eye buffer's size, fixed for
//           the session; at the session's start: updateTargetFrameRate(the
//           supported rate nearest renderconfig target_fps), where the
//           browser offers it. Every frame: XRView.requestViewportScale(scale)
//           just before three reads the view's viewport (the base layer's
//           getViewport / the binding's getViewSubImage, wrapped on the
//           instance) — the dynamic part, no buffer reallocation.
//   phone   (device.js class phone-*, not presenting) renderer.setPixelRatio(
//           native x scale), native = min(devicePixelRatio, 2) as main.js.
//   desktop flat: nothing — the controller is never fed, the scale stays 1.
// THE CONTROLLER (ResController, pure; scripts/check-adaptres.mjs): frames in
// play, in windows of window_ms. A frame is a MISS when its interval is over
// slow x the target interval (XR: the session's frame rate, else target_fps;
// phone: phone.target_fps). A frame whose own CPU part (tick start → the end
// of renderer.render) is over cpu_frac x the target is CPU-bound: its interval
// is the periods that part fills (a smaller buffer would not help it) and it
// is a MISS only when the interval is longer than that (vr-smooth: a window
// of CPU-bound frames is a clean window, so the resolution comes back once
// the load is over); an interval over
// hitch_ms (a load, a tab switch) is not counted at all. Hysteresis:
//   down  a window with more than miss_frac misses: scale - step (>= floor);
//   up    up_ms of clean windows (misses <= ok_frac): scale + step (<= 1);
//   a down within up_ms after an up doubles the wait for the next up (to
//   up_max_ms; the oscillation brake), a clean up halves it back toward up_ms.
// Config: ADAPT_DEFAULTS, overridden key by key by renderconfig.adaptive_res
// when present (none today: renderconfig is a bake-id file — its knobs fold in
// at the next change that moves the bake id anyway). resScale(): the scale now
// (1 when off) for the telemetry's res_scale.
// THE GOVERNOR (GENSURF self-protection, APP_GENSURF §4c; GovController,
// pure): the same windows / hysteresis / brake as ResController, over a
// LADDER of notches instead of a scale — march steps, then volume range, then
// gen ms (tunables gensurf.governor <rung>.notches, each notch k = 1 - (1 -
// min_k) x i / notches), and only below its last notch the resolution
// controller above. Recovery in reverse: resolution back to 1 first, then the
// notches. govFactors() -> {steps, vol, gen}: what gensched.js multiplies the
// live values by (bounded by the ceilings: it only lowers). It runs where the
// resolution does (VR, phones) and on desktop flat only with governor.flat
// (target flat_target_fps; the resolution stays untouched there). Config:
// setGovernor (gensched.js applyGenTunables, live); null / enabled false = off.
// THE DRAW RUNG (quest-budget 2026-11-01, APP_PLAYER "Automatic resolution"):
// a CPU-bound frame (session 870, Quest 3: every frame — ~600 draws both eyes,
// 21 ms of submission; the controllers above count such a frame NEITHER way,
// so nothing ever moved) must still shed load. DrawController is the ladder
// over the frame's CPU part: a frame counts when its CPU part is known and
// MISSES when that part is over cpu_frac x the target interval; a missing
// window gives one notch (DRAW_DEFAULTS notches / min_k, the same window /
// hysteresis / brake), k = 1 - (1 - min_k) x level / notches. drawK() is what
// the draw consumers scale by, only ever lower: the ground tree's split px
// (/ k: coarser nodes of the same error-bounded quadtree, groundtree.js _opt)
// and the building draw cull (x k: resident tiles beyond are not drawn,
// buildings.js). Independent of the GPU ladders (a GPU-bound frame never
// moves it, a CPU-bound one never moves them); where the resolution runs
// (XR, phones), never desktop flat. A session start / end puts it at the top.
// device.js CLASS_DRAW is the class's STATIC part of the same two numbers.

import { deviceClass } from './device.js';
import { questProfile, questSw, QUEST_SW } from './questprof.js';

// THE RESOLUTION BAND per class (all-res 2026-11-02, APP_PLAYER "Automatic
// resolution" → "The resolution band"): every class without a row runs the band
// [floor, 1] from 1 as before. A row moves the session's start (home) below the
// ceiling (top) and splits the ladder there:
//   home      the scale a session starts at; home_mv instead when the session
//             draws both eyes in one pass (xrmv.js multiview); the governor's notches
//             give way only at home, the resolution under home only below its
//             last notch (as at 1 before)
//   top       the ceiling: above home the band is SHARPNESS — it climbs only
//             while the governor has given nothing way, and it is the FIRST
//             thing to give way (before any notch)
//   step_up / step_down   the climb / the drop (default: step)
//   brake     the oscillation brake's factor (default 2), up_ms / up_max_ms
//   fast_ms / fast_frac   the fast drop: once a window has run fast_ms with at
//             least 3 counted frames and more than fast_frac of them missed, it
//             drops at once (default: off — the full window decides)
// quest (Quest 3, questeyes ab1 + w17 multiview + w15 ground, APP_SURFACE_PERF
// "The Quest's eye resolution"): start at 0.7 with multiview, 0.6 per eye
// (the multiview saving is not yet measured in the headset), climb 0.05 a 6 s clean run to 1,
// drop 0.1 within a quarter second of missing; a drop right after a climb
// (within up_ms) waits 4x as long for the next climb (24 s, 96 s, then every
// 2 min: a level that failed is not retried soon — no visible pumping); the
// floor 0.6 stays the GPU-bound fallback.
export const CLASS_RES = {
  quest: { home: 0.6, home_mv: 0.7, top: 1, step_up: 0.05, step_down: 0.1, up_ms: 6000, up_max_ms: 120000, brake: 4, fast_ms: 250, fast_frac: 0.5 },
};
export const classRes = (cls) => CLASS_RES[cls] || {};

export const ADAPT_DEFAULTS = {
  enabled: true,
  floor: 0.6,          // the lowest scale (x native)
  home: 1,             // a session's start (CLASS_RES)
  top: 1,              // the ceiling (CLASS_RES)
  step: 0.1,
  fast_ms: Infinity,   // the fast drop (CLASS_RES): off
  fast_frac: 1,
  window_ms: 1000,
  slow: 1.2,           // a miss: interval > slow x the target interval
  miss_frac: 0.2,      // a window with more misses than this: down
  ok_frac: 0.02,       // a clean window: at most this many misses
  cpu_frac: 0.85,      // a frame's CPU part over this x the target: CPU-bound, not counted
  hitch_ms: 250,       // an interval over this: not counted
  up_ms: 4000,         // clean time before a step up
  up_max_ms: 32000,    // the oscillation brake's cap
  brake: 2,            // the brake: a down within up_ms of an up multiplies the next wait by this
  phone: { target_fps: 60 },
};

export class ResController {
  constructor(cfg = {}) {
    this.cfg = { ...ADAPT_DEFAULTS, ...cfg };
    this.scale = this.cfg.home;
    this.lo = this.cfg.floor;                   // the band in force (adaptResTick narrows it to the ladder's place)
    this.hi = this.cfg.top;
    this.upWait = this.cfg.up_ms;
    this._n = 0; this._miss = 0; this._t = 0;   // the window: frames counted, misses, ms
    this._ok = 0;                               // clean ms since the last change
    this._sinceUp = Infinity;                   // ms since the last step up
    this._braked = false;                       // the last step up has braked already
  }

  // a frame: dtMs its interval, cpuMs its CPU part (null: unknown), targetMs
  // the target interval -> the new scale when it changed, else null
  frame(dtMs, cpuMs, targetMs) {
    const c = this.cfg;
    if (!(dtMs > 0) || dtMs > c.hitch_ms || !(targetMs > 0)) return null;
    this._sinceUp += dtMs;
    this._t += dtMs;
    if (!(cpuMs > c.cpu_frac * targetMs)) {
      this._n++;
      if (dtMs > c.slow * targetMs) this._miss++;
    } else {
      // CPU-bound (vr-smooth, session 872): the frame is phase-locked to the
      // periods its CPU part fills (cpu / (cpu_frac x target), rounded up) and
      // that interval is the CPU's, not a miss — a LONGER one (a period the
      // GPU or a between-frames task added) is. Counting it keeps the window
      // alive: a session that is CPU-bound in every frame no longer freezes
      // the resolution where the load phase left it (0.6 for five minutes)
      this._n++;
      if (dtMs > c.slow * Math.ceil(cpuMs / (c.cpu_frac * targetMs)) * targetMs) this._miss++;
    }
    // the fast drop (CLASS_RES): a window already missing past fast_frac
    // after fast_ms decides now — a quarter second of judder, not a second
    if (this._t >= c.fast_ms && this._t < c.window_ms && this._n >= 3 && this._miss / this._n > c.fast_frac) this._t = c.window_ms;
    return this._verdict();
  }

  // the window's verdict once it is full: the new scale when it changed, else null
  _verdict() {
    const c = this.cfg;
    if (this._t < c.window_ms) return null;
    const n = this._n, frac = n ? this._miss / n : 0, t = this._t;
    this._n = this._miss = 0; this._t = 0;
    if (!n) return null;                         // no frame with a verdict
    if (frac > c.miss_frac) {
      this._ok = 0;
      // the brake: a drop within up_ms of a climb = that climb failed — once
      // per climb (a second drop of the same overload is not a second failure)
      if (this._sinceUp < c.up_ms && !this._braked) { this.upWait = Math.min(c.up_max_ms, this.upWait * c.brake); this._braked = true; }
      return this._set(this.scale - (c.step_down ?? c.step));
    }
    if (frac > c.ok_frac) { this._ok = 0; return null; }
    this._ok += t;
    if (this._ok < this.upWait || this.scale >= this.hi) return null;
    this._ok = 0;
    // the last climb held (no drop braked it): the wait halves back
    if (this._sinceUp >= c.up_ms && !this._braked) this.upWait = Math.max(c.up_ms, this.upWait / 2);
    this._sinceUp = 0;
    this._braked = false;
    return this._set(this.scale + (c.step_up ?? c.step));
  }

  _set(s) {
    const v = Math.round(Math.min(this.hi, Math.max(this.lo, s)) * 1000) / 1000;
    if (v === this.scale) return null;
    this.scale = v;
    return v;
  }

  // a new context (a session starts / ends): full resolution, a fresh window
  reset() {
    this.scale = this.cfg.home;
    this.upWait = this.cfg.up_ms;
    this._n = this._miss = this._t = this._ok = 0;
    this._sinceUp = Infinity;
  }
}

export const GOV_RUNGS = ['steps', 'vol', 'gen'];

export const DRAW_DEFAULTS = { notches: 3, min_k: 0.5 };   // the draw rung: 3 notches to half

// the ladder: [{rung, k}] in the order the rungs give way
export function govLadder(g = {}, rungs = GOV_RUNGS) {
  const out = [];
  for (const r of rungs) {
    const c = g[r] || {}, n = Math.max(0, Math.floor(c.notches || 0)), lo = Math.min(1, Math.max(0, c.min_k ?? 1));
    for (let i = 1; i <= n; i++) out.push({ rung: r, k: Math.round((1 - (1 - lo) * i / n) * 1000) / 1000 });
  }
  return out;
}
// the factors at a ladder level (0 = none given way)
export function ladderFactors(ladder, level) {
  const f = { steps: 1, vol: 1, gen: 1 };
  for (const e of ladder) f[e.rung] = 1;
  for (let i = 0; i < Math.min(level, ladder.length); i++) f[ladder[i].rung] = ladder[i].k;
  return f;
}

export class GovController extends ResController {
  constructor(g = {}, rungs = GOV_RUNGS) {
    const ladder = govLadder(g, rungs);
    super({ ...g, floor: 0, step: ladder.length ? 1 / ladder.length : 1 });
    this.ladder = ladder;
  }
  // the scale is 1 - level / N, exactly (no float drift over the notches)
  _set(s) {
    const n = this.ladder.length;
    if (!n) return null;
    const lv = Math.min(n, Math.max(0, Math.round((1 - s) * n)));
    const v = 1 - lv / n;
    if (v === this.scale) return null;
    this.scale = v;
    return v;
  }
  get level() { return Math.round((1 - this.scale) * this.ladder.length); }
  get bottom() { return this.level >= this.ladder.length; }
  factors() { return ladderFactors(this.ladder, this.level); }
}

// the draw rung's ladder over the frame's CPU part (header): cfg = the
// ResController keys + {notches, min_k} (DRAW_DEFAULTS)
export class DrawController extends GovController {
  constructor(cfg = {}) {
    const d = { ...DRAW_DEFAULTS, ...cfg };
    super({ ...ADAPT_DEFAULTS, ...d, draw: { notches: d.notches, min_k: d.min_k } }, ['draw']);
  }
  // a frame counts only with a known CPU part; the miss is the CPU part over
  // cpu_frac x the target (dt only tells a hitch from a frame)
  frame(dtMs, cpuMs, targetMs) {
    const c = this.cfg;
    if (!(dtMs > 0) || dtMs > c.hitch_ms || !(targetMs > 0)) return null;
    this._sinceUp += dtMs;
    this._t += dtMs;
    if (cpuMs != null && cpuMs >= 0) {
      this._n++;
      if (cpuMs > c.cpu_frac * targetMs) this._miss++;
    }
    return this._verdict();
  }
  get k() { return this.factors().draw; }
}

let _gov = null, _govKey = '';
const _govLs = [];
export function onGovernor(fn) { _govLs.push(fn); }
const _govMoved = () => { for (const f of _govLs) f(); };
// the governor's config (tunables gensurf.governor); the same config keeps
// the ladder's place, a new one starts it at the top
export function setGovernor(g) {
  const key = g && g.enabled !== false ? JSON.stringify(g) : '';
  if (key === _govKey) return;
  const was = _gov && _gov.level;
  _govKey = key;
  _gov = key ? new GovController(g) : null;
  if (was) _govMoved();
}
export function govFactors() { return { ...(_gov ? _gov.factors() : { steps: 1, vol: 1, gen: 1 }), draw: drawK() }; }
export function govState() { return _gov ? { level: _gov.level, of: _gov.ladder.length, ...govFactors() } : null; }

// the draw rung (header): one ladder per install, the listeners (buildings.js
// re-plans its ring) told on every move
let _draw = null;
const _drawLs = [];
export function onDraw(fn) { _drawLs.push(fn); }
const _drawMoved = () => { for (const f of _drawLs) f(); };
// the draw factor in force (1: full / off): the ground px divides by it, the building draw cull multiplies
export function drawK() { return _draw ? _draw.k : 1; }
// THE CPU-SHORT CADENCE (all-res 2026-11-02): the Quest, or any device whose
// draw rung has measured its CPU short (a notch given way: phones and XR, where
// the rung runs) — the readers land their work later, never make it different
// (structs2 far drape, aerialway far chair lines; APP_SURFACE_PERF)
export const cpuShort = () => deviceClass() === 'quest' || drawK() < 1;
export function drawState() { return _draw ? { level: _draw.level, of: _draw.ladder.length, k: _draw.k } : null; }
function drawReset() {
  if (!_draw) return;
  const was = _draw.level;
  _draw.reset();
  if (was) _drawMoved();
}

// the supported frame rate nearest `want` (null: none offered)
export function pickFrameRate(supported, want) {
  const r = [...(supported || [])].filter((v) => v > 0);
  if (!r.length || !(want > 0)) return null;
  return r.reduce((a, b) => (Math.abs(b - want) < Math.abs(a - want) ? b : a));
}

// the mode for a device class while (not) presenting: 'xr' | 'phone' | 'off'
export function adaptMode(cls, presenting, enabled = true) {
  if (!enabled) return 'off';
  if (presenting) return 'xr';
  return /^phone/.test(cls || '') ? 'phone' : 'off';
}

let _st = null;

// the session's controller: the class row (CLASS_RES) under the Quest picture profile's band
// (questprof.js `res`: home / top over the row), from its home (home_mv on a multiview session)
function bandController(st, mv) {
  const cls = st.cls ?? deviceClass(), qp = questProfile(cls);
  // a space-warp session (spacewarp.js granted, questprof.js questSw): the SW band over the profile's
  const row = { ...classRes(cls), ...((qp && qp.res) || {}), ...(qp && questSw() ? QUEST_SW.res : {}) };
  return new ResController({ ...st.cfg, ...row, home: (mv && row.home_mv) || row.home || st.cfg.home });
}
// the owner switched the Quest picture profile (VR menu): a presenting session restarts its band at the
// new home (as a session start does); the governor / draw rungs stay where they are
export function resBandRefresh() {
  const st = _st;
  if (!st || !(st.renderer.xr && st.renderer.xr.isPresenting)) return false;
  st.ctl = bandController(st, st.mv);
  return true;
}

// a new context (a session starts / ends): the ladder back at the top
function govReset() {
  if (!_gov || !_gov.level) { if (_gov) _gov.reset(); return; }
  _gov.reset();
  _govMoved();
}

// once, after the renderer's XR setup and before any session; rc: renderconfig;
// env (scripts/check-adaptres.mjs): { cls, dpr(), now() }
export function installAdaptRes(renderer, rc = {}, env = {}) {
  const cfg = { ...ADAPT_DEFAULTS, ...(rc.adaptive_res || {}) };
  _draw = new DrawController({ ...cfg, ...(cfg.draw || {}) });
  const st = {
    renderer, cfg, ctl: new ResController(cfg), cls: env.cls,   // undefined: device.js decides (it may run later)
    dpr: env.dpr ?? (() => (typeof devicePixelRatio !== 'undefined' ? devicePixelRatio : 1)),
    clock: env.now ?? (() => performance.now()),   // the tick's clock (main.js: performance.now)
    targetFps: rc.target_fps || 72, prev: 0, start: 0, renderEnd: 0, applied: 1, mv: false,
  };
  _st = st;
  const xr = renderer.xr;
  if (xr && xr.setFramebufferScaleFactor && rc.framebuffer_scale > 0) xr.setFramebufferScaleFactor(rc.framebuffer_scale);
  // the CPU part of a frame ends with its render call
  const render = renderer.render.bind(renderer);
  renderer.render = (s, c) => { render(s, c); st.renderEnd = st.clock(); };
  if (xr && xr.addEventListener) {
    xr.addEventListener('sessionstart', () => {
      // the session's band: the class row (CLASS_RES); its home_mv when the
      // session draws both eyes in one pass (xrmv.js, the vendored three's
      // isMultiview) — a per-eye session starts at the row's plain home
      const mv = !!(xr.isMultiview && xr.isMultiview());
      st.mv = mv;
      st.ctl = bandController(st, mv);
      govReset();
      drawReset();
      const s = xr.getSession();
      const r = s && s.updateTargetFrameRate ? pickFrameRate(s.supportedFrameRates, st.targetFps) : null;
      if (r) s.updateTargetFrameRate(r).catch(() => {});
      // the view scale goes in just before three reads each view's viewport
      const ask = (view) => { if (cfg.enabled && view && view.requestViewportScale) view.requestViewportScale(st.ctl.scale); };
      const layer = xr.getBaseLayer && xr.getBaseLayer(), bind = xr.getBinding && xr.getBinding();
      if (layer && typeof layer.getViewport === 'function') {
        const g = layer.getViewport.bind(layer);
        layer.getViewport = (view) => { ask(view); return g(view); };
      }
      if (bind && typeof bind.getViewSubImage === 'function') {
        const g = bind.getViewSubImage.bind(bind);
        bind.getViewSubImage = (lay, view, eye) => { ask(view); return g(lay, view, eye); };
      }
    });
    // three restores the pixel ratio it saved at the start: the phone's
    // controller resumes from the scale that ratio carries
    xr.addEventListener('sessionend', () => { st.ctl = new ResController(cfg); st.ctl.scale = st.applied; st.mv = false; govReset(); drawReset(); });
  }
  return st;
}

// every frame, near the tick's start: now (ms), play (in play: the
// controller counts only then)
export function adaptResTick(now, play) {
  const st = _st;
  if (!st) return;
  const dt = st.prev ? now - st.prev : 0, cpu = st.renderEnd > st.start && st.start ? st.renderEnd - st.start : null;
  st.prev = st.start = now;
  const xr = st.renderer.xr, presenting = !!(xr && xr.isPresenting);
  const mode = adaptMode(st.cls ?? deviceClass(), presenting, st.cfg.enabled);
  const gov = _gov && (mode !== 'off' || _gov.cfg.flat) ? _gov : null;
  if ((mode === 'off' && !gov) || !play) return;
  let target;
  // space warp (questSw): the browser paces the app at half the display rate — the target is two periods
  if (mode === 'xr') { const s = xr.getSession(); target = (questSw() ? 2000 : 1000) / ((s && s.frameRate) || st.targetFps); }
  else if (mode === 'phone') target = 1000 / ((st.cfg.phone && st.cfg.phone.target_fps) || 60);
  else target = 1000 / (gov.cfg.flat_target_fps || 60);
  // the draw rung: the frame's CPU part, where the resolution runs (never
  // desktop flat), beside the GPU ladders below and independent of them
  if (mode !== 'off' && _draw && _draw.frame(dt, cpu, target) != null) _drawMoved();
  // the ladder: the governor's notches while the resolution is full, the
  // resolution only below its last notch (both fed there: a clean window
  // lifts a notch, a missing one lowers the resolution)
  // (THE RESOLUTION BAND, CLASS_RES: the notches give way at home only; above
  // home the sharpness climbs while no notch has given way and is the first to
  // go; under home only below the last notch — home = top = 1 is the old rule)
  const c = st.ctl, home = c.cfg.home;
  if (gov && (mode === 'off' || Math.abs(c.scale - home) < 1e-9) && gov.frame(dt, cpu, target) != null) _govMoved();
  if (mode === 'off') return;
  c.lo = gov && !gov.bottom ? home : c.cfg.floor;
  c.hi = gov && gov.level > 0 ? home : c.cfg.top;
  if (c.lo >= c.hi && Math.abs(c.scale - home) < 1e-9) return;   // nothing can move: the window is not fed
  const v = c.frame(dt, cpu, target);
  if (v == null || mode !== 'phone') return;
  st.renderer.setPixelRatio(Math.min(st.dpr(), 2) * v);
  st.applied = v;
}

// the scale in force (1: full / off) — the telemetry's res_scale
export function resScale() {
  if (!_st) return 1;
  const xr = _st.renderer.xr;
  return xr && xr.isPresenting ? _st.ctl.scale : _st.applied;
}
